import { useEffect, useState } from "react";
import { useLocation, Link, Routes, Route } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Clock,
  Award,
  Hammer,
  Home,
  Paintbrush,
  Thermometer,
  Star,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  CheckCircle2,
  ArrowRight,
  Send,
  Mountain,
  Users,
  Zap,
  Droplets,
} from "lucide-react";
import { REGIONS, BUSINESS, getServices, getReasons } from "./data/regions";
import { CITY_PAGES, getCityPageBySlug } from "./data/cityPages";
import CityPage from "./pages/CityPage";
import BeforeAfterSection from "./components/BeforeAfterSection";
import MentionsLegales from "./pages/MentionsLegales";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
import NotFoundPage from "./pages/NotFoundPage";
import AdminPanel from "./pages/AdminPanel";
import CookieConsentBanner from "./components/CookieConsentBanner";
import WhatsAppButton from "./components/WhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const IconMap = {
  Home,
  Hammer,
  Paintbrush,
  Thermometer,
  Droplets,
  MapPin,
  ShieldCheck,
  Clock,
  Award,
  CheckCircle2,
};

// Sticky Mobile CTA Component
const StickyMobileCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#C2410C] p-3 shadow-2xl animate-fade-in" data-testid="sticky-mobile-cta">
      <a
        href={BUSINESS.phoneLink}
        className="flex items-center justify-center gap-3 text-white font-bold"
      >
        <Phone className="w-5 h-5 animate-pulse" />
        <span className="uppercase tracking-wider">Appeler {BUSINESS.phone}</span>
      </a>
    </div>
  );
};

// Reassurance Bar Component
const ReassuranceBar = () => (
  <div className="bg-[#0F172A] py-4" data-testid="reassurance-bar">
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex flex-wrap justify-center gap-6 md:gap-12">
        {[
          { icon: ShieldCheck, text: "Garantie décennale" },
          { icon: Clock, text: "Devis gratuit 24h" },
          { icon: Zap, text: "Intervention rapide" },
          { icon: Users, text: "Artisan local" },
        ].map((item, index) => (
          <div key={index} className="flex items-center gap-2 text-white/90">
            <item.icon className="w-4 h-4 text-[#C2410C]" />
            <span className="text-sm font-medium">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Region Selector Component
const RegionSelector = ({ currentRegion, scrolled }) => {
  const regions = Object.values(REGIONS);
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className={`flex items-center gap-2 text-sm font-medium uppercase tracking-wider ${
            scrolled ? "text-[#0F172A] hover:text-[#C2410C]" : "text-white hover:text-[#C2410C]"
          }`}
          data-testid="region-selector"
        >
          <MapPin className="w-4 h-4" />
          {currentRegion.shortName}
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {regions.map((region) => (
          <DropdownMenuItem key={region.id} asChild>
            <Link 
              to={region.slug} 
              className={`flex items-center gap-2 ${region.id === currentRegion.id ? "bg-muted" : ""}`}
            >
              <MapPin className="w-4 h-4 text-[#C2410C]" />
              <div>
                <p className="font-medium">{region.name}</p>
                <p className="text-xs text-muted-foreground">{region.fullName}</p>
              </div>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Navigation Component
const Navigation = ({ region }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "À propos" },
    { href: "#avant-apres", label: "Avant/Après" },
    { href: "#gallery", label: "Réalisations" },
    { href: "#testimonials", label: "Avis" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={region.slug} className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 bg-[#C2410C] flex items-center justify-center">
              {region.id === "haute-savoie" ? (
                <Mountain className="w-6 h-6 text-white" />
              ) : (
                <Home className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <span
                className={`font-bold text-xl tracking-tight uppercase font-['Barlow_Condensed'] ${
                  scrolled ? "text-[#0F172A]" : "text-white"
                }`}
              >
                BKR Toiture
              </span>
              <span
                className={`block text-xs tracking-wider ${
                  scrolled ? "text-[#64748B]" : "text-white/70"
                }`}
              >
                {region.shortName}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors ${
                  scrolled
                    ? "text-[#0F172A] hover:text-[#C2410C]"
                    : "text-white hover:text-[#C2410C]"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side: Region Selector + Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <RegionSelector currentRegion={region} scrolled={scrolled} />
            <a
              href={BUSINESS.phoneLink}
              className="phone-highlight flex items-center gap-2 hover:bg-[#9A3412] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>{BUSINESS.phone}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? (
              <X className={scrolled ? "text-[#0F172A]" : "text-white"} />
            ) : (
              <Menu className={scrolled ? "text-[#0F172A]" : "text-white"} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden mobile-menu absolute top-20 left-0 right-0 p-6">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-white text-lg font-medium uppercase tracking-wider py-2 border-b border-white/10"
              >
                {link.label}
              </a>
            ))}
            {/* Mobile Region Links */}
            <div className="pt-4 border-t border-white/20">
              <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Nos zones</p>
              {Object.values(REGIONS).map((r) => (
                <Link
                  key={r.id}
                  to={r.slug}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 text-white ${r.id === region.id ? "font-bold" : "text-white/70"}`}
                >
                  {r.fullName}
                </Link>
              ))}
            </div>
            <a
              href={BUSINESS.phoneLink}
              className="phone-highlight flex items-center justify-center gap-2 mt-4"
            >
              <Phone className="w-4 h-4" />
              <span>{BUSINESS.phone}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section with improved SEO content
const HeroSection = ({ region }) => {
  const { hero } = region;
  
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={hero.image}
          alt={`Artisan couvreur ${region.name}`}
          className="w-full h-full object-cover hero-image"
        />
        <div className="hero-overlay absolute inset-0"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-32 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <Badge
            className="mb-6 bg-[#C2410C] text-white border-none uppercase tracking-wider text-xs px-4 py-2 animate-fade-in"
            data-testid="hero-badge"
          >
            {hero.badge}
          </Badge>

          {/* Main Heading - SEO Optimized H1 */}
          <h1
            className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight leading-none mb-6 animate-fade-in-up font-['Barlow_Condensed']"
            data-testid="hero-title"
          >
            {hero.title}
            <br />
            <span className="text-[#C2410C]">{hero.titleAccent}</span> {hero.location}
            <span className="text-[#C2410C]"> {hero.locationAccent}</span>
          </h1>

          {/* Subheading */}
          <p
            className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl leading-relaxed animate-fade-in-up stagger-2"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
            <a href="#contact">
              <Button
                className="cta-primary h-14 px-8 text-base uppercase font-bold tracking-widest"
                data-testid="hero-cta-devis"
              >
                Recevoir mon devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href={BUSINESS.phoneLink}>
              <Button
                variant="outline"
                className="cta-outline h-14 px-8 text-base uppercase font-bold tracking-widest text-white border-white hover:bg-white hover:text-[#0F172A]"
                data-testid="hero-cta-appeler"
              >
                <Phone className="mr-2 w-5 h-5" />
                Appeler maintenant
              </Button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap gap-6 animate-fade-in-up stagger-4">
            <div className="flex items-center gap-2 text-white/80">
              <ShieldCheck className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">Garantie décennale</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">Devis sous 24h</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Award className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">{region.id === "haute-savoie" ? "Expert alpin" : "Artisan qualifié"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Contact Card */}
      <div className="hidden lg:block absolute bottom-20 right-16 bg-white p-8 shadow-2xl border-t-4 border-[#C2410C] max-w-sm animate-slide-in-right">
        <h3 className="font-bold text-xl uppercase tracking-tight mb-4 font-['Barlow_Condensed']">
          Contact rapide
        </h3>
        <div className="space-y-3">
          <a
            href={BUSINESS.phoneLink}
            className="flex items-center gap-3 text-[#0F172A] hover:text-[#C2410C] transition-colors"
          >
            <Phone className="w-5 h-5 text-[#C2410C]" />
            <span className="font-semibold">{BUSINESS.phone}</span>
          </a>
          <a
            href={`mailto:${BUSINESS.email}`}
            className="flex items-center gap-3 text-[#0F172A] hover:text-[#C2410C] transition-colors"
          >
            <Mail className="w-5 h-5 text-[#C2410C]" />
            <span>{BUSINESS.email}</span>
          </a>
          <div className="flex items-center gap-3 text-[#64748B]">
            <MapPin className="w-5 h-5 text-[#C2410C]" />
            <span>{region.location}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// SEO Content Section (new)
const SEOContentSection = ({ region }) => {
  const isAlpine = region.id === "haute-savoie";
  
  return (
    <section className="py-12 bg-white border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="prose prose-lg max-w-none text-[#64748B]">
          {isAlpine ? (
            <p>
              <strong className="text-[#0F172A]">BKR Toiture, votre artisan couvreur en Haute-Savoie</strong>, 
              intervient dans tout le département du 74 et la région de Genève pour vos travaux de toiture, 
              charpente et ravalement de façade. Spécialiste des toitures de montagne, nous maîtrisons les 
              contraintes spécifiques du climat alpin : résistance aux charges de neige, étanchéité renforcée, 
              isolation thermique performante. De Annecy à Chamonix, en passant par Annemasse et Thonon-les-Bains, 
              notre équipe d'artisans qualifiés vous garantit un travail soigné et durable. 
              <strong className="text-[#0F172A]"> Devis gratuit sous 24h, garantie décennale sur tous nos travaux.</strong>
            </p>
          ) : (
            <p>
              <strong className="text-[#0F172A]">BKR Toiture, artisan couvreur dans les Yvelines (78)</strong>, 
              réalise tous vos travaux de couverture, charpente et façade en Île-de-France. Basés à Le Pecq, 
              nous intervenons rapidement à Saint-Germain-en-Laye, Versailles, Poissy, Le Vésinet, Chatou et 
              dans toutes les communes des Yvelines. Fort de plus de 10 ans d'expérience, nous mettons notre 
              savoir-faire au service de votre patrimoine : rénovation de toiture, traitement de charpente, 
              ravalement de façade, isolation des combles. 
              <strong className="text-[#0F172A]"> Contactez-nous pour un devis gratuit sous 24h. Garantie décennale.</strong>
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ region }) => {
  const { about } = region;
  
  return (
    <section
      id="about"
      className="py-20 md:py-28 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <img
              src={about.image}
              alt="Artisan couvreur professionnel"
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#C2410C] text-white p-6 hidden md:block">
              <div className="text-4xl font-black font-['Barlow_Condensed']">
                {about.experience}
              </div>
              <div className="text-sm uppercase tracking-wider">
                {about.experienceLabel}
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <Badge className="mb-4 bg-[#F1F5F9] text-[#64748B] border-none uppercase tracking-wider text-xs">
              À propos de nous
            </Badge>
            <h2
              className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-8 font-['Barlow_Condensed']"
              data-testid="about-title"
              dangerouslySetInnerHTML={{ __html: about.title.replace(/\n/g, '<br />') }}
            />
            <div className="space-y-4 text-[#64748B] leading-relaxed">
              {about.paragraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>

            {/* Key points */}
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                "Artisan qualifié et certifié",
                "Matériaux haut de gamme",
                "Intervention rapide",
                "Devis détaillé gratuit",
              ].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C2410C] flex-shrink-0" />
                  <span className="text-[#0F172A] font-medium">{point}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-8">
              <a href="#contact">
                <Button className="cta-primary h-12 px-6 uppercase font-bold tracking-wider">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = ({ region }) => {
  const services = getServices(region.id);
  
  return (
    <section
      id="services"
      className="py-20 md:py-28 bg-[#F8FAFC]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-white text-[#64748B] border-[#E2E8F0] uppercase tracking-wider text-xs">
            Nos expertises
          </Badge>
          <h2
            className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4 font-['Barlow_Condensed'] mx-auto inline-block"
            data-testid="services-title"
          >
            Nos prestations
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mt-8">
            {region.id === "haute-savoie" 
              ? "Solutions complètes adaptées au climat alpin pour votre toiture, charpente et façade en Haute-Savoie."
              : "Des solutions complètes pour l'entretien et la rénovation de votre toiture, charpente et façade dans les Yvelines."
            }
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = IconMap[service.icon];
            return (
              <Card
                key={index}
                className={`service-card group ${
                  service.featured
                    ? "md:col-span-2 md:row-span-2 bg-[#1E293B] text-white"
                    : "bg-white"
                }`}
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8 h-full flex flex-col">
                  <div
                    className={`service-icon w-14 h-14 flex items-center justify-center mb-6 ${
                      service.featured ? "bg-[#C2410C]" : "bg-[#F1F5F9]"
                    }`}
                  >
                    <Icon
                      className={`w-7 h-7 ${
                        service.featured ? "text-white" : "text-[#C2410C]"
                      }`}
                    />
                  </div>

                  <h3
                    className={`text-2xl font-bold uppercase tracking-tight mb-4 font-['Barlow_Condensed'] ${
                      service.featured ? "text-white" : "text-[#0F172A]"
                    }`}
                  >
                    {service.title}
                  </h3>

                  <p
                    className={`mb-6 leading-relaxed ${
                      service.featured ? "text-white/80" : "text-[#64748B]"
                    }`}
                  >
                    {service.description}
                  </p>

                  <ul className="space-y-2 mt-auto">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-[#C2410C]" />
                        <span
                          className={
                            service.featured
                              ? "text-white/90 text-sm"
                              : "text-[#0F172A] text-sm"
                          }
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA after services */}
        <div className="mt-12 text-center">
          <a href="#contact">
            <Button className="cta-primary h-12 px-8 uppercase font-bold tracking-wider">
              Demander un devis gratuit
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = ({ region }) => {
  const reasons = getReasons(region.id);

  return (
    <section
      id="why-us"
      className="py-20 md:py-28 bg-white"
      data-testid="why-choose-us-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <Badge className="mb-4 bg-[#F1F5F9] text-[#64748B] border-none uppercase tracking-wider text-xs">
              Nos engagements
            </Badge>
            <h2
              className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-8 font-['Barlow_Condensed']"
              data-testid="why-choose-us-title"
            >
              Pourquoi nous
              <br />
              faire confiance ?
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-8">
              {region.id === "haute-savoie"
                ? "Chez BKR Toiture, notre expertise du climat alpin fait la différence. Nous comprenons les défis spécifiques des toitures en montagne et apportons des solutions durables adaptées à votre environnement."
                : "Chez BKR Toiture, nous nous engageons à fournir un service de qualité supérieure. Notre réputation dans les Yvelines repose sur notre professionnalisme, notre réactivité et la satisfaction de nos clients."
              }
            </p>

            <a href="#contact">
              <Button
                className="cta-primary h-12 px-6 uppercase font-bold tracking-wider"
                data-testid="why-choose-us-cta"
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>

          <div className="space-y-4">
            {reasons.map((reason, index) => {
              const Icon = IconMap[reason.icon];
              return (
                <div
                  key={index}
                  className="trust-badge bg-white p-6 shadow-sm"
                  data-testid={`trust-badge-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#C2410C]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-[#0F172A] mb-1 font-['Barlow_Condensed'] uppercase tracking-tight">
                        {reason.title}
                      </h3>
                      <p className="text-[#64748B] text-sm">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials, region }) => {
  return (
    <section
      id="testimonials"
      className="py-20 md:py-28 bg-[#1E293B] zinc-texture"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 relative z-10">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-[#C2410C] text-white border-none uppercase tracking-wider text-xs">
            Témoignages clients
          </Badge>
          <h2
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']"
            data-testid="testimonials-title"
          >
            Ils nous font confiance
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            {region.id === "haute-savoie"
              ? "Découvrez les avis de nos clients en Haute-Savoie et région de Genève."
              : "Découvrez les avis de nos clients satisfaits dans les Yvelines et en Île-de-France."
            }
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id || index}
              className="testimonial-card bg-white/5 backdrop-blur-sm border border-white/10 p-8"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex gap-1 mb-4 relative z-10">
                {[...Array(testimonial.note)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]"
                  />
                ))}
              </div>

              <p className="text-white/90 leading-relaxed mb-6 relative z-10 font-serif italic">
                {testimonial.commentaire}
              </p>

              <div className="relative z-10">
                <p className="font-bold text-white font-['Barlow_Condensed'] uppercase tracking-tight">
                  {testimonial.nom}
                </p>
                <p className="text-white/60 text-sm">
                  {testimonial.ville} • {testimonial.date}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA after testimonials */}
        <div className="mt-12 text-center">
          <a href="#contact">
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] h-12 px-8 uppercase font-bold tracking-wider text-white">
              Recevoir mon devis gratuit
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = ({ gallery, region }) => {
  const [filter, setFilter] = useState("Tous");
  const categories = ["Tous", ...new Set(gallery.map((item) => item.categorie))];
  const filteredGallery = filter === "Tous" ? gallery : gallery.filter((item) => item.categorie === filter);

  return (
    <section
      id="gallery"
      className="py-20 md:py-28 bg-[#F8FAFC]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-white text-[#64748B] border-[#E2E8F0] uppercase tracking-wider text-xs">
            Portfolio
          </Badge>
          <h2
            className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4 font-['Barlow_Condensed'] mx-auto inline-block"
            data-testid="gallery-title"
          >
            Nos réalisations
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mt-8">
            {region.id === "haute-savoie"
              ? "Découvrez nos travaux en Haute-Savoie : chalets, maisons de montagne et bâtiments alpins."
              : "Découvrez nos travaux de couverture, charpente et façade réalisés dans les Yvelines."
            }
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={filter === cat ? "default" : "outline"}
              className={`uppercase text-xs tracking-wider font-bold ${
                filter === cat
                  ? "bg-[#0F172A] text-white"
                  : "border-[#E2E8F0] text-[#64748B] hover:bg-[#0F172A] hover:text-white"
              }`}
              onClick={() => setFilter(cat)}
              data-testid={`gallery-filter-${cat}`}
            >
              {cat}
            </Button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGallery.map((item, index) => (
            <div
              key={item.id || index}
              className="gallery-item relative overflow-hidden group cursor-pointer"
              data-testid={`gallery-item-${index}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.titre}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-[#0F172A]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <Badge className="w-fit mb-2 bg-[#C2410C] text-white border-none text-xs uppercase">
                  {item.categorie}
                </Badge>
                <h3 className="text-white font-bold text-xl font-['Barlow_Condensed'] uppercase tracking-tight">
                  {item.titre}
                </h3>
                <p className="text-white/70 text-sm mt-1">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Area Section
const ServiceAreaSection = ({ region }) => {
  const { serviceArea } = region;
  const cityPages = Object.values(CITY_PAGES).filter(c => c.region === region.id);

  return (
    <section
      id="zone"
      className="py-20 md:py-28 bg-white"
      data-testid="service-area-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="map-container h-[400px] lg:h-[500px] overflow-hidden">
            <iframe
              src={serviceArea.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Zone d'intervention BKR Toiture ${region.name}`}
            ></iframe>
          </div>

          <div>
            <Badge className="mb-4 bg-[#F1F5F9] text-[#64748B] border-none uppercase tracking-wider text-xs">
              Couverture géographique
            </Badge>
            <h2
              className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-8 font-['Barlow_Condensed']"
              data-testid="service-area-title"
            >
              {serviceArea.title}
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: serviceArea.description }} />
            <p className="text-[#64748B] leading-relaxed mb-8">
              {serviceArea.secondParagraph}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {serviceArea.areas.map((area, index) => (
                <div key={index} className="flex items-center gap-2 text-[#0F172A]">
                  <MapPin className="w-4 h-4 text-[#C2410C] flex-shrink-0" />
                  <span className="text-sm font-medium">{area}</span>
                </div>
              ))}
            </div>

            {/* City Pages Links */}
            {cityPages.length > 0 && (
              <div className="mt-8 p-4 bg-[#F8FAFC] border border-[#E2E8F0]">
                <p className="text-sm font-medium text-[#0F172A] mb-3">Pages dédiées par ville :</p>
                <div className="flex flex-wrap gap-2">
                  {cityPages.map((city) => (
                    <Link
                      key={city.slug}
                      to={city.slug}
                      className="text-sm text-[#C2410C] hover:text-[#9A3412] underline"
                    >
                      Couvreur {city.city}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Improved Contact Section
const ContactSection = ({ region }) => {
  const [formData, setFormData] = useState({
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    type_travaux: "",
    message: "",
    region: region.id,
    honeypot: "", // Anti-spam field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitTime, setSubmitTime] = useState(0);

  // Track form load time for anti-spam
  useEffect(() => {
    setSubmitTime(Date.now());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-spam checks
    if (formData.honeypot) {
      console.log("Spam detected - honeypot filled");
      return;
    }
    
    // Check if form was submitted too quickly (less than 3 seconds)
    if (Date.now() - submitTime < 3000) {
      toast.error("Veuillez patienter quelques secondes avant de soumettre.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { honeypot, ...submitData } = formData;
      const response = await axios.post(`${API}/contact`, submitData);
      if (response.data.success) {
        toast.success("Demande envoyée avec succès !", {
          description: "Nous vous recontacterons sous 24h pour votre devis gratuit.",
          duration: 5000,
        });
        setFormData({
          nom: "",
          telephone: "",
          email: "",
          adresse: "",
          type_travaux: "",
          message: "",
          region: region.id,
          honeypot: "",
        });
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez nous appeler directement au " + BUSINESS.phone,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-[#F8FAFC]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <div>
            <Badge className="mb-4 bg-white text-[#64748B] border-[#E2E8F0] uppercase tracking-wider text-xs">
              Contactez-nous
            </Badge>
            <h2
              className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-8 font-['Barlow_Condensed']"
              data-testid="contact-title"
            >
              Devis Gratuit
              <br />
              Sous 24h
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-4">
              Besoin d'un devis pour vos travaux de <strong className="text-[#0F172A]">toiture, charpente ou façade</strong> {region.id === "haute-savoie" ? "en Haute-Savoie ou région de Genève" : "dans les Yvelines"} ?
            </p>
            <p className="text-[#64748B] leading-relaxed mb-8">
              <strong className="text-[#C2410C]">Réponse garantie sous 24 heures.</strong> Notre équipe vous rappelle rapidement pour étudier votre projet et vous proposer une solution adaptée à vos besoins et votre budget.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              <a
                href={BUSINESS.phoneLink}
                className="flex items-center gap-4 p-4 bg-[#C2410C] text-white hover:bg-[#9A3412] transition-colors shadow-lg"
                data-testid="contact-phone-card"
              >
                <div className="w-12 h-12 bg-white/20 flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wider opacity-90">
                    Appelez-nous
                  </p>
                  <p className="font-bold text-xl">
                    {BUSINESS.phone}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-4 p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
                data-testid="contact-email-card"
              >
                <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B] uppercase tracking-wider">
                    Email
                  </p>
                  <p className="font-bold text-lg text-[#0F172A]">
                    {BUSINESS.email}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-white shadow-sm">
                <div className="w-12 h-12 bg-[#F1F5F9] flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#C2410C]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B] uppercase tracking-wider">
                    Zone
                  </p>
                  <p className="font-bold text-lg text-[#0F172A]">
                    {region.location}
                  </p>
                </div>
              </div>
            </div>

            {/* Guarantee Badge */}
            <div className="flex items-center gap-4 p-6 bg-[#0F172A] text-white">
              <ShieldCheck className="w-10 h-10 text-[#C2410C]" />
              <div>
                <p className="font-bold text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                  Garantie Décennale
                </p>
                <p className="text-white/70 text-sm">
                  Tous nos travaux sont assurés
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white p-8 shadow-lg">
            <h3 className="text-2xl font-bold uppercase tracking-tight mb-2 font-['Barlow_Condensed']">
              Recevoir mon devis gratuit
            </h3>
            <p className="text-[#64748B] text-sm mb-6">
              Remplissez ce formulaire, nous vous recontactons sous 24h pour étudier votre projet.
            </p>
            
            <form
              onSubmit={handleSubmit}
              className="contact-form space-y-4"
              data-testid="contact-form"
            >
              {/* Honeypot - hidden field for spam protection */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                    Nom complet *
                  </label>
                  <Input
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                    className="h-12 rounded-none border-[#E2E8F0]"
                    data-testid="contact-input-nom"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                    Téléphone *
                  </label>
                  <Input
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    placeholder="06 XX XX XX XX"
                    className="h-12 rounded-none border-[#E2E8F0]"
                    data-testid="contact-input-telephone"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Email *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="votre@email.com"
                  className="h-12 rounded-none border-[#E2E8F0]"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Adresse du chantier *
                </label>
                <Input
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  required
                  placeholder="Adresse complète du chantier"
                  className="h-12 rounded-none border-[#E2E8F0]"
                  data-testid="contact-input-adresse"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Type de travaux *
                </label>
                <Select
                  value={formData.type_travaux}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type_travaux: value })
                  }
                  required
                >
                  <SelectTrigger
                    className="h-12 rounded-none border-[#E2E8F0]"
                    data-testid="contact-select-type"
                  >
                    <SelectValue placeholder="Sélectionnez le type de travaux" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="couverture">Couverture / Toiture</SelectItem>
                    <SelectItem value="charpente">Charpente</SelectItem>
                    <SelectItem value="facade">Ravalement de façade</SelectItem>
                    <SelectItem value="isolation">Isolation</SelectItem>
                    <SelectItem value="reparation">Réparation urgente</SelectItem>
                    {region.id === "haute-savoie" && (
                      <SelectItem value="chalet">Rénovation chalet</SelectItem>
                    )}
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Message (optionnel)
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet en quelques mots..."
                  rows={4}
                  className="rounded-none border-[#E2E8F0] resize-none"
                  data-testid="contact-textarea-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="cta-primary w-full h-14 text-base uppercase font-bold tracking-widest"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></span>
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Recevoir mon devis gratuit
                  </span>
                )}
              </Button>

              <p className="text-[#64748B] text-xs text-center">
                En soumettant ce formulaire, vous acceptez d'être recontacté par nos services. 
                Vos données sont confidentielles et ne seront jamais partagées.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Improved Footer with SEO text
const Footer = ({ region }) => {
  const currentYear = new Date().getFullYear();
  const { serviceArea } = region;
  const otherRegion = Object.values(REGIONS).find(r => r.id !== region.id);
  const cityPages = Object.values(CITY_PAGES).filter(c => c.region === region.id);

  const seoFooterText = region.id === "haute-savoie"
    ? "BKR Toiture est votre artisan couvreur de confiance en Haute-Savoie (74). Spécialiste des toitures de montagne, nous intervenons à Annecy, Annemasse, Thonon-les-Bains, Chamonix, Megève et dans tout le département pour vos travaux de couverture, charpente et ravalement de façade. Nos équipes maîtrisent les contraintes du climat alpin et vous garantissent des prestations durables. Devis gratuit, garantie décennale."
    : "BKR Toiture, artisan couvreur basé à Le Pecq, intervient dans tout le département des Yvelines (78) et l'Île-de-France. Rénovation de toiture, charpente traditionnelle, ravalement de façade et isolation : nous mettons notre savoir-faire au service de votre patrimoine. Intervention rapide à Saint-Germain-en-Laye, Versailles, Poissy, Le Vésinet et toutes les communes environnantes. Devis gratuit sous 24h.";

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="footer">
      {/* SEO Footer Text */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-8">
          <p className="text-white/60 text-sm leading-relaxed max-w-4xl">
            {seoFooterText}
          </p>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-[#C2410C] flex items-center justify-center">
                {region.id === "haute-savoie" ? (
                  <Mountain className="w-6 h-6 text-white" />
                ) : (
                  <Home className="w-6 h-6 text-white" />
                )}
              </div>
              <span className="font-bold text-2xl tracking-tight uppercase font-['Barlow_Condensed']">
                BKR Toiture
              </span>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              {region.id === "haute-savoie"
                ? "Artisan couvreur, charpentier et façadier spécialiste du climat alpin. Intervention en Haute-Savoie (74), région d'Annecy, Annemasse et Grand Genève."
                : "Artisan couvreur, charpentier et façadier basé à Le Pecq (78). Nous intervenons dans tout le département des Yvelines et en Île-de-France pour vos travaux de toiture et rénovation."
              }
            </p>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="w-6 h-6 text-[#C2410C]" />
              <span className="font-semibold">Garantie Décennale</span>
            </div>
            <Link 
              to={otherRegion.slug}
              className="inline-flex items-center gap-2 text-[#C2410C] hover:text-white transition-colors text-sm"
            >
              <MapPin className="w-4 h-4" />
              Voir aussi : BKR Toiture {otherRegion.name}
            </Link>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg uppercase tracking-tight mb-6 font-['Barlow_Condensed']">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={BUSINESS.phoneLink}
                  className="footer-link flex items-center gap-3 text-white/80 hover:text-white"
                >
                  <Phone className="w-4 h-4 text-[#C2410C]" />
                  {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="footer-link flex items-center gap-3 text-white/80 hover:text-white"
                >
                  <Mail className="w-4 h-4 text-[#C2410C]" />
                  {BUSINESS.email}
                </a>
              </li>
              <li>
                <span className="flex items-center gap-3 text-white/80">
                  <MapPin className="w-4 h-4 text-[#C2410C]" />
                  {region.location}
                </span>
              </li>
            </ul>

            {/* City Pages */}
            {cityPages.length > 0 && (
              <div className="mt-6">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Pages locales</p>
                <ul className="space-y-1">
                  {cityPages.map((city) => (
                    <li key={city.slug}>
                      <Link 
                        to={city.slug}
                        className="text-white/70 hover:text-[#C2410C] text-sm transition-colors"
                      >
                        Couvreur {city.city}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Zone */}
          <div>
            <h4 className="font-bold text-lg uppercase tracking-tight mb-6 font-['Barlow_Condensed']">
              Zone d'intervention
            </h4>
            <ul className="space-y-2 text-white/70">
              {serviceArea.footerAreas.map((area, i) => (
                <li key={i}>{area}</li>
              ))}
              <li className="text-[#C2410C]">{serviceArea.footerExtra}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-6 pb-20 lg:pb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} BKR Toiture - Artisan Couvreur {region.name}. Tous droits réservés.
            </p>
            <div className="flex gap-6 text-white/50 text-sm">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Regional Page Component
const RegionalPage = ({ region }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/testimonials`, { params: { region: region.id } }),
          axios.get(`${API}/gallery`, { params: { region: region.id } }),
        ]);
        setTestimonials(testimonialsRes.data);
        setGallery(galleryRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [region.id]);

  useEffect(() => {
    const { seo } = region;
    document.title = seo.title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", seo.description);

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", seo.keywords);

    window.scrollTo(0, 0);
  }, [region]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="text-center">
          <div className="spinner w-12 h-12 border-4 border-white/30 border-t-[#C2410C] rounded-full mx-auto mb-4"></div>
          <p className="text-white/70">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation region={region} />
      <HeroSection region={region} />
      <ReassuranceBar />
      <SEOContentSection region={region} />
      <AboutSection region={region} />
      <ServicesSection region={region} />
      <BeforeAfterSection region={region} />
      <WhyChooseUsSection region={region} />
      <TestimonialsSection testimonials={testimonials} region={region} />
      <GallerySection gallery={gallery} region={region} />
      <ServiceAreaSection region={region} />
      <ContactSection region={region} />
      <Footer region={region} />
      <StickyMobileCTA />
    </>
  );
};

// City Page Wrapper
const CityPageWrapper = () => {
  const location = useLocation();
  const cityData = getCityPageBySlug(location.pathname);
  
  if (!cityData) {
    return null;
  }

  const region = cityData.region === "haute-savoie" ? REGIONS.hauteSavoie : REGIONS.yvelines;

  return (
    <>
      <Navigation region={region} />
      <CityPage cityData={cityData} region={region} />
      <Footer region={region} />
      <StickyMobileCTA />
    </>
  );
};

// Main App Component
function App() {
  const location = useLocation();
  
  // Legal pages
  const legalPages = ["/mentions-legales", "/politique-de-confidentialite"];
  const isLegalPage = legalPages.includes(location.pathname);
  const isAdminPage = location.pathname === "/admin";

  // Check if current path is a city page
  const isCityPage = Object.values(CITY_PAGES).some(city => city.slug === location.pathname);
  
  // Known paths
  const knownPaths = ["/", "/haute-savoie", "/admin", ...legalPages, ...Object.values(CITY_PAGES).map(c => c.slug)];
  const is404 = !knownPaths.includes(location.pathname);

  // Determine region based on URL
  const getRegionFromPath = (pathname) => {
    if (pathname === "/haute-savoie") {
      return REGIONS.hauteSavoie;
    }
    return REGIONS.yvelines;
  };

  const currentRegion = getRegionFromPath(location.pathname);

  // 404 page
  if (is404) {
    return (
      <div className="App" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <NotFoundPage />
      </div>
    );
  }

  // Admin page
  if (isAdminPage) {
    return (
      <div className="App" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <AdminPanel />
      </div>
    );
  }

  // Legal pages
  if (isLegalPage) {
    return (
      <div className="App" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <Navigation region={REGIONS.yvelines} />
        {location.pathname === "/mentions-legales" && <MentionsLegales />}
        {location.pathname === "/politique-de-confidentialite" && <PolitiqueConfidentialite />}
        <Footer region={REGIONS.yvelines} />
        <WhatsAppButton />
        <CookieConsentBanner />
      </div>
    );
  }

  // If it's a city page, render CityPageWrapper
  if (isCityPage) {
    return (
      <div className="App" data-testid="app-container">
        <Toaster position="top-right" richColors />
        <CityPageWrapper />
        <WhatsAppButton />
        <CookieConsentBanner />
      </div>
    );
  }

  // Otherwise render regional page
  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <RegionalPage region={currentRegion} />
      <WhatsAppButton />
      <CookieConsentBanner />
    </div>
  );
}

export default App;
